/**
 * Company List Page
 * หน้ารายการบริษัท - Matching reference design
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useCompanies, useCompaniesStore } from './'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Plus, ChevronRight, ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePermissions } from '@/features/auth/usePermissions'
import { CompanyStatsCards } from './components/CompanyStatsCards'
import { CompanyListSkeleton } from './components/CompanyListSkeleton'
import { CreateCompanyDialog } from './components/CreateCompanyDialog'
import type { Company } from './types'

// Status badge component matching design
function StatusBadge({ status }: { status: Company['status'] }) {
  const { t } = useTranslation()

  const statusStyles = {
    'in-progress': 'bg-[var(--status-in-progress-bg)] text-[var(--status-in-progress-text)]',
    completed: 'bg-[var(--status-completed-bg)] text-[var(--status-completed-text)]',
    'not-started': 'bg-[var(--status-not-started-bg)] text-[var(--status-not-started-text)]',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium',
        statusStyles[status]
      )}
    >
      {t(`status.${status}`)}
    </span>
  )
}

// Format date based on current language
function formatDate(dateString: string, i18n: { language: string }): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString

  const isThai = i18n.language === 'th'

  return new Intl.DateTimeFormat(isThai ? 'th-TH' : 'en-US', {
    day: 'numeric',
    month: 'short',
    year: isThai ? 'numeric' : '2-digit',
  }).format(date)
}

export function CompanyListPage() {
  const { t, i18n } = useTranslation()
  const { can } = usePermissions()
  const [createOpen, setCreateOpen] = useState(false)

  const filters = useCompaniesStore((state) => state.filters)
  const setSearch = useCompaniesStore((state) => state.setSearch)
  const setStatus = useCompaniesStore((state) => state.setStatus)
  const setPage = useCompaniesStore((state) => state.setPage)

  const { data, isLoading, error } = useCompanies(filters)

  if (isLoading) return <CompanyListSkeleton />

  if (error) {
    return (
      <div className="flex h-[calc(100vh-8rem)] flex-col items-center justify-center gap-4">
        <p className="text-destructive">{t('companies.errors.loadFailed')}</p>
        <Button
          onClick={() => {
            window.location.reload()
          }}
        >
          {t('actions.retry')}
        </Button>
      </div>
    )
  }

  const companies = data?.companies ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / filters.pageSize))

  // Calculate counts for filter tabs
  const inProgressCount = companies.filter((c) => c.status === 'in-progress').length
  const completedCount = companies.filter((c) => c.status === 'completed').length
  const notStartedCount = companies.filter((c) => c.status === 'not-started').length

  const tabs = [
    { key: 'all', label: t('status.all'), count: total },
    { key: 'in-progress', label: t('status.inProgress'), count: inProgressCount },
    { key: 'completed', label: t('status.completed'), count: completedCount },
    { key: 'not-started', label: t('status.notStarted'), count: notStartedCount },
  ] as const

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">{t('companies.title')}</h1>
          <span className="text-muted-foreground">{t('companies.management')}</span>
          <span className="bg-primary/10 text-primary rounded px-2 py-0.5 text-sm font-medium">
            {t('pagination.of', { total })} {t('companies.unit')}
          </span>
        </div>
        {can('companies:create') && (
          <Button
            onClick={() => {
              setCreateOpen(true)
            }}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('companies.create')}
          </Button>
        )}
      </div>

      {/* Stats cards */}
      <CompanyStatsCards companies={companies} total={total} />

      {/* Search and Filter Section */}
      <div className="bg-card mb-6 rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search Input */}
          <div className="min-w-[300px] flex-1">
            <SearchInput
              placeholder={t('companies.searchPlaceholder')}
              value={filters.search}
              onChange={(value: string) => {
                setSearch(value)
              }}
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setStatus(tab.key)
                }}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  filters.status === tab.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                {tab.label}
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0 text-[10px]',
                    filters.status === tab.key
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-background text-muted-foreground'
                  )}
                >
                  ({tab.count})
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Company Table */}
      <div className="bg-card overflow-hidden rounded-lg border">
        {companies.length === 0 ? (
          <div className="text-muted-foreground p-8 text-center">{t('companies.noCompanies')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.company')}
                  </th>
                  <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.taxId')}
                  </th>
                  <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.businessType')}
                  </th>
                  <th className="text-muted-foreground px-4 py-2 text-center text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.projects')}
                  </th>
                  <th className="text-muted-foreground px-4 py-2 text-center text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.latestYear')}
                  </th>
                  <th className="text-muted-foreground px-4 py-2 text-center text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.status')}
                  </th>
                  <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.contactPerson')}
                  </th>
                  <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.updated')}
                  </th>
                  <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
                    {t('companies.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {companies.map((company) => (
                  <tr key={company.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2">
                      <div>
                        <p className="text-primary text-xs font-semibold">{company.nameTh}</p>
                        <p className="text-muted-foreground text-[10px]">{company.nameEn}</p>
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-[10px]">{company.taxId}</td>
                    <td className="text-muted-foreground px-4 py-2 text-[10px]">
                      {company.businessType}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="bg-muted inline-flex items-center rounded px-2 py-0.5 text-[10px]">
                        {company.projectCount} {t('project.projectsUnit')}
                      </span>
                    </td>
                    <td className="text-primary px-4 py-2 text-center text-xs font-bold">
                      {company.latestYear ?? '-'}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <StatusBadge status={company.status} />
                    </td>
                    <td className="px-4 py-2 text-xs">{company.contactPerson}</td>
                    <td className="text-muted-foreground px-4 py-2 text-[10px]">
                      {formatDate(company.updatedAt, i18n)}
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        asChild
                        size="sm"
                        className="bg-primary hover:bg-primary/90 h-7 px-2 text-[10px] font-bold uppercase"
                      >
                        <Link to={`/companies/${company.id}`}>
                          {t('companies.table.viewDetails')}
                          <ChevronRight className="ml-0.5 h-3 w-3" />
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary + Pagination Footer */}
      <div className="mt-4 flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          {t('pagination.showing', {
            start: companies.length > 0 ? (filters.page - 1) * filters.pageSize + 1 : 0,
            end: Math.min(filters.page * filters.pageSize, total),
            total,
          })}
        </p>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setPage(filters.page - 1)
              }}
              disabled={filters.page <= 1}
              className="h-7 w-7 p-0"
              aria-label={t('pagination.previous')}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - filters.page) <= 1)
              .reduce<(number | '...')[]>((acc, p, i, arr) => {
                const prev = arr[i - 1]
                if (i > 0 && prev !== undefined && prev < p - 1) acc.push('...')
                acc.push(p)
                return acc
              }, [])
              .map((p, i) =>
                p === '...' ? (
                  <span
                    key={`ellipsis-${String(i)}`}
                    className="text-muted-foreground px-1 text-xs"
                  >
                    …
                  </span>
                ) : (
                  <Button
                    key={p}
                    size="sm"
                    variant={filters.page === p ? 'default' : 'outline'}
                    onClick={() => {
                      setPage(p)
                    }}
                    className="h-7 w-7 p-0 text-xs"
                  >
                    {p}
                  </Button>
                )
              )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setPage(filters.page + 1)
              }}
              disabled={filters.page >= totalPages}
              className="h-7 w-7 p-0"
              aria-label={t('pagination.next')}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Create Company Dialog */}
      {can('companies:create') && (
        <CreateCompanyDialog
          open={createOpen}
          onClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
    </div>
  )
}
